import { Moment } from 'moment';
import { sortByDates } from '@shared/utils/sortByDates';
import { toMoment } from '@shared/utils/date';
import { Values } from './objectEnums';
import { HireTypes } from './constants';

type PeriodSchedule = {
	id: number;
	from: Moment;
	to: Moment;
	hireRate: number;
	hireType: Values<typeof HireTypes>;
};

const getPeriodFromDate = (date: Moment, periodSchedule: PeriodSchedule[]): PeriodSchedule | null => {
	if (periodSchedule == null || periodSchedule.length === 0 || date == null) {
		return null;
	}

	// Sort rate schedules by from date
	const sortedSchedules = sortByDates(periodSchedule, 'from');

	// Find the first rate schedule that encompasses the given date
	const matchingSchedule = sortedSchedules.find((schedule) => (
		date.isSameOrAfter(schedule.from) && date.isSameOrBefore(schedule.to)
	));

	if (matchingSchedule) {
		return matchingSchedule;
	}

	// If the date is outside all schedules, use the latest rate or 0
	const lastSchedule = sortedSchedules[sortedSchedules.length - 1];
	const firstSchedule = sortedSchedules[0];

	if (date.isBefore(firstSchedule.from)) {
		return firstSchedule;
	}

	return date.isAfter(lastSchedule.to) ? lastSchedule : null;
};

/**
 * Resolves the rate schedule item for a hire period start date.
 * When the date falls on a period boundary (same instant as a period's `to`),
 * the following period is used if it starts at that instant.
 */
export const getPeriodFromDateForHire = (
	date: Moment,
	periodSchedule: PeriodSchedule[],
): PeriodSchedule | null => {
	let periodScheduleItem = getPeriodFromDate(date, periodSchedule);

	if (periodScheduleItem?.to && date.isSame(periodScheduleItem.to)) {
		const periodTo = periodScheduleItem.to;
		const next = periodSchedule.find(
			(rs) => rs.from && toMoment(rs.from).isSame(periodTo),
		);

		if (next) {
			periodScheduleItem = next;
		}
	}

	return periodScheduleItem;
};

export default getPeriodFromDate;
